import {
  Close,
} from "@mui/icons-material";
import {
  DialogActions,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import React from "react";
import { translate } from "../../translate/translate";
import { RFCExecution } from "../../models/general/import";
import { RFC_TYPE_LABEL, useRFCDialog } from "./useRFCDialog";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface RFCDialogProps {
  open: boolean;
  onClose: () => void;
  rfcsap: RFCExecution | null | undefined;
}

const RFCDialog = ({ open, onClose, rfcsap }: RFCDialogProps) => {
  const {
    showMessage,
    showAllMaterials,
    setShowAllMaterials,
    isBOM,
    shouldShowDetails,
    errorMessageDisplay,
    getStatusLabel,
    getStatusColor,
    formatDateTime,
    formatDuration,
    handleCopy,
  } = useRFCDialog(rfcsap);

  const formatBeginDate = (dateString?: string): string => {
    if (!dateString) return "---";

    if (dateString.length === 8 && /^\d{8}$/.test(dateString)) {
      const year = dateString.substring(0, 4);
      const month = dateString.substring(4, 6);
      const day = dateString.substring(6, 8);
      return `${day}/${month}/${year}`;
    }

    return "---";
  };

  const formatExecutionParameters = (
    params?: Record<string, any> | null
  ): React.ReactNode => {
    if (!params || typeof params !== "object") {
      return <Typography>---</Typography>;
    }

    const plant = params.plant;
    const beginDate = params.begin_date;
    const materials: string[] = Array.isArray(params.materials)
      ? params.materials
      : [];

    const parameterItemSx = {
      pl: 3,
      textIndent: "-14px",
      display: "block",
      whiteSpace: "normal",
    };

    const chunkArray = (array: string[], size: number) => {
      const result: string[][] = [];
      for (let i = 0; i < array.length; i += size) {
        result.push(array.slice(i, i + size));
      }
      return result;
    };

    const getMaterialsPreviewArray = (
      materials: string[],
      limit = 5
    ): string[] => {
      return materials.slice(0, limit);
    };

    return (
      <Grid container direction="column" gap={0.5}>
        <Typography sx={parameterItemSx}>
          • <b>{translate.t("plant")}:</b> {plant || "---"}
        </Typography>

        {rfcsap?.rfc_type === "FORECAST" && (
          <Typography sx={parameterItemSx}>
            • <b>{translate.t("date")}:</b> {formatBeginDate(beginDate)}
          </Typography>
        )}

        {materials.length > 0 && (
          <>
            <Typography sx={parameterItemSx}>
              • <b>{translate.t("material")}:</b>
            </Typography>

            {(() => {
              const visibleMaterials = showAllMaterials
                ? materials
                : getMaterialsPreviewArray(materials, 6);

              const lines = chunkArray(visibleMaterials, 6);
              const lastLineIndex = lines.length - 1;

              return lines.map((line, index) => (
                <Typography
                  key={index}
                  sx={{
                    pl: 5,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    flexWrap: "wrap",
                  }}
                >
                  <span>{line.join(", ")}</span>

                  {index === lastLineIndex && materials.length > 6 && (
                    <ExpandMoreIcon
                      onClick={() => setShowAllMaterials((prev) => !prev)}
                      sx={{
                        fontSize: 18,
                        cursor: "pointer",
                        transition: "transform 0.2s ease",
                        transform: showAllMaterials
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        color: "primary.main",
                        ml: 0.3,
                      }}
                    />
                  )}
                </Typography>
              ));
            })()}
          </>
        )}
      </Grid>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: 420,
          maxHeight: "85vh",
        },
      }}
    >
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        py={1.5}
        px={2}
        sx={{ backgroundColor: "#4B00B5" }}
      >
        <Typography sx={{ color: "#fff", fontWeight: "bold" }}>
          {translate.t("execution_log")}
        </Typography>
        <Close sx={{ color: "#fff", cursor: "pointer" }} onClick={onClose} />
      </Grid>

      <DialogContent sx={{ p: 1.5 }}>
        <Grid container direction="column" gap={1}>
          <Typography>
            <b>{translate.t("rfc")}:</b>{" "}
            {RFC_TYPE_LABEL[rfcsap?.rfc_type || ""] ||
              rfcsap?.rfc_type ||
              "---"}
          </Typography>

          <Grid container direction="column" gap={0.5}>
            <Typography fontWeight="bold">
              {translate.t("parameters")}:
            </Typography>
            {formatExecutionParameters(rfcsap?.execution_parameters)}
          </Grid>

          <Typography>
            <b>{translate.t("user")}:</b>{" "}
            {rfcsap?.initiated_by_label || "---"}
          </Typography>

          <Typography>
            <b>{translate.t("start_datetime")}:</b>{" "}
            {formatDateTime(rfcsap?.started_at)}
          </Typography>

          <Typography>
            <b>{translate.t("execution_time")}:</b>{" "}
            {formatDuration(rfcsap?.duration_seconds)}
          </Typography>

          <Typography>
            <b>{translate.t("end_datetime")}:</b>{" "}
            {formatDateTime(rfcsap?.finished_at)}
          </Typography>

          <Typography sx={{ mt: 0.5 }}>
            <b>{translate.t("status")}:</b>{" "}
            <span
              style={{
                color: getStatusColor(rfcsap?.status),
                fontWeight: "bold",
              }}
            >
              {getStatusLabel(rfcsap?.status)}
            </span>
          </Typography>


          {shouldShowDetails && (
            <>
              <Grid container alignItems="center" mt={0.5}>
                <Grid
                  item
                  onClick={handleCopy}
                  sx={{
                    ml: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: "pointer",
                  }}
                >
                  <ContentCopyOutlinedIcon fontSize="small" color="primary" />
                  <Typography color="primary" fontWeight="bold">
                    {showMessage
                      ? translate.t("copied")
                      : translate.t("copy")}
                  </Typography>
                </Grid>
              </Grid>

              <TextField
                multiline
                rows={5}
                value={errorMessageDisplay}
                disabled
                sx={{
                  mt: 0.5,
                  backgroundColor: "#FAFAFA",
                  "& .MuiInputBase-input": {
                    color: "#DC2626",
                    WebkitTextFillColor: "#DC2626",
                  },
                }}
              />
            </>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 1.5 }}>
        <Button variant="outlined" onClick={onClose}>
          Fechar
        </Button>

        {isBOM && (
          <Button variant="contained" color="primary" disabled>
            {translate.t("reprocess_failures")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RFCDialog;
